import React from "react";
import Link from "next/link";
import ApiService from "../../../services/ApiService";
import { SectionTitle, sharedStyles } from "./_shared";
import renderIcon from "../../../components/renderIcon";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

const articlesStripStyles = `
.tr-articles__list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: clamp(16px, 2vw, 24px);
  justify-content: center;
  max-width: 1100px;
  margin: 0 auto;
}
@media (max-width: 900px) {
  .tr-articles__list { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 560px) {
  .tr-articles__list { grid-template-columns: minmax(0, 420px); }
}

.tr-articles__card {
  display: flex;
  flex-direction: column;
  border-radius: 18px;
  overflow: hidden;
  background: white;
  border: 1px solid #e5e7eb;
  text-decoration: none;
  color: inherit;
  transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
}
.tr-articles__card:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 32px rgba(0,0,0,.1);
  border-color: #d1d5db;
}

.tr-articles__imgwrap {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #f3f4f6;
  overflow: hidden;
}
.tr-articles__img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  transition: transform .4s ease;
}
.tr-articles__card:hover .tr-articles__img { transform: scale(1.04); }
.tr-articles__img-placeholder {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  color: var(--primary, #1b579e);
  background: color-mix(in srgb, var(--primary, #1b579e) 8%, white);
}

.tr-articles__body {
  padding: 16px 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
}
.tr-articles__credit {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: var(--primary, #1b579e);
}
.tr-articles__title {
  font-size: 17px;
  font-weight: 800;
  color: #1a1a2e;
  line-height: 1.3;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-wrap: balance;
}
.tr-articles__date {
  margin-top: auto;
  padding-top: 10px;
  font-size: 12px;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
`;

function buildLangPrefix(lang) {
    const v = lang?.value || (typeof lang === "string" ? lang : null);
    return v ? "/" + v : "";
}

// ArticlesStrip : grille de 3 articles "Étude et Inspirations".
// Source : API /auto-content/preview?lang=X (alignée sur themes/2026 et Ummah).
// Si org.limited, homepage.showArticles=false ou liste vide -> section masquée.
// Footer : lien "Voir tous les articles" vers /jewish-educations, affiché
// uniquement si une page CONTENT_HUB existe dans org.pages.
export default function ArticlesStrip({ context }) {
    const { org, travel, lang, langValue } = context;
    const enabled = !org?.limited && travel?.homepage?.showArticles !== false;
    const [data, setData] = React.useState([]);

    React.useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        (async () => {
            try {
                const lv = langValue || lang?.value || "fr";
                const res = await ApiService.get("/auto-content/preview?lang=" + lv);
                if (!cancelled && Array.isArray(res?.data)) {
                    setData(res.data.slice(0, 3));
                }
            } catch (err) {
                // silencieux : preview optionnelle
            }
        })();
        return () => { cancelled = true; };
    }, [enabled, org?._id, langValue, lang?.value]);

    if (!enabled || data.length === 0) return null;

    const hasContentHub = (org.pages || []).some(p => p.type === "CONTENT_HUB");
    const allArticlesHref = org.BASE_WEB_URL + "/jewish-educations";

    const resolvedTitle = t`Étude et Inspirations`;
    const resolvedHighlight = t`Inspirations`;
    const resolvedSubtitle = t`Articles, enseignements et nouvelles à découvrir`;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: sharedStyles + articlesStripStyles }} />
            <section className="tr-ui-section">
                <SectionTitle title={resolvedTitle} highlight={resolvedHighlight} subtitle={resolvedSubtitle} />

                <div className="tr-articles__list">
                    {data.map(item => {
                        const isExternal = item.source && item.source !== "LOUBAVITCH";
                        const href = isExternal
                            ? item.source_link
                            : `${org.BASE_WEB_URL}/article/${item._id}`;
                        const credit = Array.isArray(item.credits) && item.credits.length > 0 ? item.credits[0] : null;

                        const inner = (
                            <>
                                <span style={{display:"block"}} className="tr-articles__imgwrap">
                                    {item.image ? (
                                        <img src={item.image} alt={item.title || ""} className="tr-articles__img" loading="lazy" />
                                    ) : (
                                        <span style={{display:"block"}} className="tr-articles__img-placeholder">
                                            {renderIcon({ name: "IconBook2", source: "tabler" }, 48)}
                                        </span>
                                    )}
                                </span>

                                <span style={{display:"block"}} className="tr-articles__body">
                                    {credit && (
                                        <span className="tr-articles__credit">{credit.name}</span>
                                    )}
                                    <h3 className="tr-articles__title">{item.title}</h3>
                                    {item.datetime && (
                                        <span className="tr-articles__date">
                                            {renderIcon({ name: "IconClock", source: "tabler" }, 12)}
                                            {new Date(item.datetime).toLocaleDateString(lang?.value || langValue || "fr", { day: "numeric", month: "long", year: "numeric" })}
                                        </span>
                                    )}
                                </span>
                            </>
                        );

                        if (isExternal) {
                            return (
                                <a key={item._id} href={href} target="_blank" rel="noopener noreferrer" className="tr-articles__card">
                                    {inner}
                                </a>
                            );
                        }

                        return (
                            <Link key={item._id} href={href} legacyBehavior>
                                <a className="tr-articles__card">
                                    {inner}
                                </a>
                            </Link>
                        );
                    })}
                </div>

                {hasContentHub && (
                    <Link href={allArticlesHref} legacyBehavior>
                        <a className="tr-ui-seemore">
                            <span><Trans>Voir plus d'articles</Trans></span>
                            <span className="tr-ui-seemore__arrow">
                                {renderIcon({ name: "IconArrowRight", source: "tabler" }, 14)}
                            </span>
                        </a>
                    </Link>
                )}
            </section>
        </>
    );
}
